// src/modules/app-views/invoices/pages/Document/actions/floatButton.js
// *******************************************************************************
// INVOICES FLOAT BUTTON ACTIONS
// *******************************************************************************
// Float button actions for invoice document operations.
// Actions visibility/enabled rules:
// - Validate: available unless invoice is in final state (authorized, canceled, denied)
// - Emit: only for draft/rejected (can retry), hidden for authorized/canceled/pending
// - Download PDF/XML: only for authorized/canceled (has accessKey)
// - Send Email/Whatsapp: only for authorized (active invoice)
// - Cancel: only for authorized (within legal deadline - 24h typically)
// - Complementary: only for authorized
// - Print: always available for authorized/canceled
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  SendOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  MailOutlined,
  WhatsAppOutlined,
  CloseCircleOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  SafetyCertificateOutlined,
  CloudUploadOutlined,
  FileProtectOutlined,
  DislikeOutlined,
  AuditOutlined,
  EditOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import { onProduction } from 'utils/helpers';

// *******************************************************************************
// FLOAT BUTTON ACTIONS
// *******************************************************************************

/**
 * Build float button actions array for invoice document
 */
const invoicesDocumentFloatButton = (context = {}) => {
  const { data, issuer, isEmitting, handlers = {} } = context;

  // Check if issuer is active for invoice emission actions (validate/emit only)
  const isIssuerActive = issuer?.fiscalData?.isActive === true;

  // If invoice is being emitted, lock all actions
  if (isEmitting) {
    return [];
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // STATUS CHECKS
  // ─────────────────────────────────────────────────────────────────────────────
  const isNew = !data?._id || data?._id === 'add';
  const invoiceStatus = data?.status;
  const isDraft = !invoiceStatus || invoiceStatus === 'draft';
  const isAuthorized = invoiceStatus === 'authorized';
  const isCanceled = invoiceStatus === 'canceled';
  const isRejected = invoiceStatus === 'rejected';
  const isDenied = invoiceStatus === 'denied';
  const isCorrected = invoiceStatus === 'corrected';
  const hasAccessKey = !!data?.accessKey;
  // NFC-e (model 65) does not support complementary or devolution invoices
  const isNfe = data?.documentType === 'nfe';

  // Check if invoice is in a final state (cannot be validated anymore)
  const isFinalState = isAuthorized || isCanceled || isDenied;

  // Check if invoice can receive correction letter
  const canReceiveCorrectionLetter = isAuthorized || isCorrected;

  // Check if document validation passed
  const isValidated = data?.documentValidation?.isValid === true;

  // ─────────────────────────────────────────────────────────────────────────────
  // BUILD ACTIONS ARRAY
  // ─────────────────────────────────────────────────────────────────────────────
  const result = [
    // EMIT INVOICE - Primary action (highlighted) - Requires active issuer, disabled when not validated
    isIssuerActive &&
    !isNew &&
    (isDraft || isRejected) && {
      key: 'emit',
      icon: <CloudUploadOutlined />,
      tooltipText: 'emit_invoice',
      type: 'primary',
      disabled: !isValidated,
      onClick: handlers.onEmit,
    },

    // VALIDATE INVOICE - Requires active issuer, available unless in final state
    isIssuerActive &&
    !isNew &&
    !isFinalState && {
      key: 'validate',
      icon: <FileProtectOutlined />,
      tooltipText: 'validate_invoice',
      onClick: handlers.onValidate,
    },

    // DOWNLOAD PDF
    /*
    hasAccessKey && {
      key: 'downloadPdf',
      icon: <FilePdfOutlined />,
      tooltipText: 'download_pdf',
      onClick: handlers.onDownloadPdf,
    },
*/

    // DOWNLOAD XML
    /*
    hasAccessKey && {
      key: 'downloadXml',
      icon: <FileTextOutlined />,
      tooltipText: 'download_xml',
      onClick: handlers.onDownloadXml,
    },
*/

    // PRINT DOCUMENT - Only available after emission (has accessKey)
    hasAccessKey && {
      key: 'printDocument',
      icon: <PrinterOutlined />,
      tooltipText: 'print_document',
      type: 'primary',
      onClick: handlers.onPrint,
    },

    // SEND EMAIL
    isAuthorized &&
    !onProduction() && {
      key: 'sendEmail',
      icon: <MailOutlined />,
      tooltipText: 'send_by_email',
      type: 'primary',
      onClick: handlers.onSendEmail,
    },

    // SEND WHATSAPP
    isAuthorized &&
    !onProduction() && {
      key: 'sendWhatsapp',
      icon: <WhatsAppOutlined />,
      tooltipText: 'send_by_whatsapp',
      type: 'primary',
      onClick: handlers.onSendWhatsapp,
    },

    // COMPLEMENTARY INVOICE - NF-e only (NFC-e does not support complementary)
    isAuthorized &&
    isNfe &&
    hasAccessKey && {
      key: 'complementaryInvoice',
      icon: <AuditOutlined />,
      tooltipText: 'complementary_invoice',
      onClick: handlers.onComplementary,
    },

    // DEVOLUTION / REVERSAL - NF-e generates devolution (finNFe=4), NFC-e generates NF-e de Ajuste/Estorno (finNFe=3)
    // Frontend handler distinguishes between the two based on data.documentType
    isAuthorized &&
    hasAccessKey && {
      key: 'devolutionInvoice',
      icon: <ImportOutlined />,
      tooltipText: isNfe ? 'devolution_invoice' : 'reversal_invoice',
      onClick: handlers.onDevolution,
    },

    // CANCEL INVOICE - Available for authorized invoices in all environments
    isAuthorized &&
    hasAccessKey && {
      key: 'cancelInvoice',
      icon: <DislikeOutlined />,
      tooltipText: 'cancel_invoice',
      onClick: handlers.onCancel,
    },

    // CORRECTION LETTER - Available for authorized or corrected invoices
    canReceiveCorrectionLetter &&
    hasAccessKey && {
      key: 'correctionLetter',
      icon: <EditOutlined />,
      tooltipText: 'send_correction_letter',
      onClick: handlers.onCorrectionLetter,
    },
  ].filter(Boolean);
  return result;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default invoicesDocumentFloatButton;
